// src/components/configuration/sections/team/TeamSection.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  AlertCircle,
  Plus,
  Search,
  Users,
  Filter,
  Shield,
  User
} from 'lucide-react';

// shadcn components
import { Alert, AlertDescription } from '../../../ui/alert';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Spinner } from '../../../ui/spinner';
import { ScrollArea } from '../../../ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../../ui/radio-group';

// Page container
import PageContainer from '../../../ui/layout/PageContainer/PageContainer';

// Local components
import TeamMemberListItem from './TeamMemberListItem';
import TeamMemberEditor from './TeamMemberEditor';
import { LocalSidebarRail } from '../../../ui/local-sidebar-rail';

// Hook
import { useTeam } from '../../../../hooks/useTeam';
import { cn } from '../../../../utils/cn';

const TeamSection = () => {
  // Get data from team context
  const {
    teamMembers,
    permissions,
    pendingInvitations,
    currentUser,
    updateMemberRole,
    updateTeamMember,
    inviteTeamMember,
    cancelInvitation,
    resendInvitation,
    removeTeamMember,
    reactivateTeamMember,
    loading,
    permissionsLoading,
    roleUpdateLoading,
    inviteLoading,
    savingMember,
    error,
    clearError,
    refreshTeamData,
    canManageTeam,
    canAddTeamMembers,
    canUpdateTeamRoles,
    canRemoveTeamMembers,
    canManageInvitations,
    currentRole
  } = useTeam();

  // Local UI state
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null); // 'member' | 'invitation'
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'staff',
    message: ''
  });
  const [inviteErrors, setInviteErrors] = useState({});

  // Invite loading watcher — close modal on server confirmation
  const prevInviteLoading = useRef(false);
  useEffect(() => {
    if (prevInviteLoading.current && !inviteLoading && showInviteModal) {
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'staff', message: '' });
      setInviteErrors({});
    }
    prevInviteLoading.current = inviteLoading;
  }, [inviteLoading, showInviteModal]);

  // Auto-select first item (prefer team members)
  useEffect(() => {
    if (!selectedItemId) {
      if (teamMembers && teamMembers.length > 0) {
        setSelectedItemId(teamMembers[0].id);
        setSelectedItemType('member');
      } else if (pendingInvitations && pendingInvitations.length > 0) {
        setSelectedItemId(pendingInvitations[0].id);
        setSelectedItemType('invitation');
      }
    }
  }, [teamMembers, pendingInvitations, selectedItemId]);

  // Create unified list with invitations
  const unifiedList = [
    ...pendingInvitations.map(inv => ({ ...inv, type: 'invitation' })),
    ...teamMembers.map(member => ({ ...member, type: 'member' }))
  ];

  // Filter and sort list
  const filteredList = unifiedList
    .filter(item => {
      const searchText = item.type === 'member'
        ? `${item.name} ${item.email}`.toLowerCase()
        : item.email.toLowerCase();

      const matchesSearch = !searchTerm || searchText.includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'all' || item.role === roleFilter;

      return matchesSearch && matchesRole;
    })
    .sort((a, b) => {
      // Sort: invitations first, then active members, then inactive members
      if (a.type === 'invitation' && b.type !== 'invitation') return -1;
      if (a.type !== 'invitation' && b.type === 'invitation') return 1;
      if (a.type === 'member' && b.type === 'member') {
        if (a.is_active && !b.is_active) return -1;
        if (!a.is_active && b.is_active) return 1;
      }
      return 0;
    });

  // Get selected item (member or invitation)
  const selectedItem = (() => {
    if (!selectedItemId || !selectedItemType) return null;

    if (selectedItemType === 'member') {
      const member = teamMembers.find(m => m.id === selectedItemId);
      return member ? { type: 'member', data: member } : null;
    } else if (selectedItemType === 'invitation') {
      const invitation = pendingInvitations.find(i => i.id === selectedItemId);
      return invitation ? { type: 'invitation', data: invitation } : null;
    }

    return null;
  })();

  // Handle operations
  const handleUpdateMember = (memberId, updateData) => {
    updateTeamMember(memberId, updateData);
  };

  const handleUpdateRole = (memberId, newRole) => {
    updateMemberRole(memberId, newRole);
  };

  const handleInviteMember = () => {
    const newErrors = {};
    if (!inviteForm.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(inviteForm.email)) newErrors.email = 'Invalid email';

    if (Object.keys(newErrors).length > 0) {
      setInviteErrors(newErrors);
      return;
    }

    inviteTeamMember(inviteForm.email, inviteForm.role, inviteForm.message);
  };

  // Role filter options
  const roleFilterOptions = [
    { value: 'all', label: 'All Roles', icon: Users, count: unifiedList.length },
    { value: 'admin', label: 'Administrators', icon: User, count: unifiedList.filter(i => i.role === 'admin').length },
    { value: 'staff', label: 'Staff', icon: User, count: unifiedList.filter(i => i.role === 'staff').length }
  ];

  const roleOptions = [
    { value: 'staff', label: 'Staff', description: 'Can manage customers, appointments, and invoices. Cannot access team settings or financial reports.' },
    { value: 'admin', label: 'Administrator', description: 'Full access including team management, business settings, and financial reports. Can invite and manage team members.' }
  ];

  // Permission check
  if (!canManageTeam) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-20">
          <div className="text-center p-8 bg-muted/30 border rounded-2xl">
            <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground mb-4">Only administrators can access this section.</p>
            <div className="text-sm text-muted-foreground">
              Your current role: <span className="font-medium">{currentRole || 'Unknown'}</span>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Loading state
  if (loading || permissionsLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Spinner className="size-16 text-primary mx-auto mb-4" />
            <p className="text-foreground text-lg">Loading your team...</p>
            <p className="text-muted-foreground text-sm mt-2">Retrieving team members and permissions</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer heightMode="viewport" maxWidth="full" padding="none">
      {/* Error Message */}
      {error && (
        <Alert variant="destructive" className="m-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={clearError}>
                Dismiss
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content - Split Panel Layout */}
      <div className="h-full flex">
        {/* LEFT PANEL - Team Members List (shadcn-inspired architecture) */}
        {/* Outer Spacer: Controls layout width, no overflow/border/bg */}
        <div
          className={cn(
            "relative flex-shrink-0 transition-all duration-300 ease-in-out",
            leftPanelCollapsed ? "w-0" : "w-[320px]"
          )}
        >
          {/* Inner Container: Always 320px, slides in/out */}
          <div
            className={cn(
              "absolute inset-y-0 left-0 w-[320px] border-r border-border bg-slate-900/50 flex flex-col transition-transform duration-300 ease-in-out",
              leftPanelCollapsed && "-translate-x-full"
            )}
          >
            {/* Hover Rail - Edge collapse/expand control */}
            <LocalSidebarRail
              collapsed={leftPanelCollapsed}
              onToggle={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
              side="left"
            />

            {/* Header - Administrative Layer */}
            <div className="p-3 border-b flex-shrink-0" data-layer="administrative">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold">Team Members</h2>
                {canAddTeamMembers && (
                  <Button
                    size="sm"
                    onClick={() => setShowInviteModal(true)}
                    disabled={inviteLoading}
                    className="layer-button font-medium"
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    Invite
                  </Button>
                )}
              </div>

              {/* Search Bar and Filter */}
              <div className="flex gap-2 mb-3">
                <div className="relative group flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-slate-400 transition-colors duration-300 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder="Search members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="layer-input pl-10"
                  />
                </div>

                {/* Role Filter Popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="icon" className="shrink-0">
                      <Filter className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64" align="end">
                    <div className="space-y-3">
                      <h4 className="font-semibold text-sm">Filter by Role</h4>
                      <div className="space-y-2">
                        {roleFilterOptions.map((option) => {
                          const Icon = option.icon;
                          return (
                            <button
                              key={option.value}
                              onClick={() => setRoleFilter(option.value)}
                              className={cn(
                                "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
                                roleFilter === option.value
                                  ? "bg-primary/20 text-primary font-medium"
                                  : "hover:bg-muted"
                              )}
                            >
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                <span>{option.label}</span>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {option.count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Members List - overflow contained to ScrollArea only */}
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-2">
                {filteredList.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm">
                      {searchTerm || roleFilter !== 'all' ? 'No members match your search' : 'No team members yet'}
                    </p>
                    {canAddTeamMembers && !searchTerm && roleFilter === 'all' && (
                      <Button size="sm" onClick={() => setShowInviteModal(true)} className="mt-4">
                        <Plus className="mr-2 h-3 w-3" />
                        Invite First Member
                      </Button>
                    )}
                  </div>
                ) : (
                  filteredList.map((item) => (
                    <TeamMemberListItem
                      key={`${item.type}-${item.id}`}
                      member={item}
                      type={item.type}
                      isSelected={selectedItemId === item.id && selectedItemType === item.type}
                      onClick={() => {
                        setSelectedItemId(item.id);
                        setSelectedItemType(item.type);
                      }}
                      canManageInvitations={canManageInvitations}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* RIGHT PANEL - Team Member/Invitation Editor */}
        <div className="flex-1 min-w-0">
          <TeamMemberEditor
            item={selectedItem}
            currentUser={currentUser}
            onUpdateMember={handleUpdateMember}
            onUpdateRole={handleUpdateRole}
            onRemoveMember={removeTeamMember}
            onReactivateMember={reactivateTeamMember}
            onCancelInvitation={cancelInvitation}
            onResendInvitation={resendInvitation}
            permissions={{
              canManageTeam,
              canUpdateTeamRoles,
              canRemoveTeamMembers,
              canManageInvitations
            }}
            savingMember={savingMember}
            savingRole={roleUpdateLoading}
            leftPanelCollapsed={leftPanelCollapsed}
            onToggleLeftPanel={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
          />
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <Dialog open={showInviteModal} onOpenChange={(open) => { if (!inviteLoading) setShowInviteModal(open); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                Invite Team Member
              </DialogTitle>
              <DialogDescription>
                Send an invitation to join your team with the appropriate role and permissions.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="invite_email">Email Address</Label>
                <Input
                  id="invite_email"
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => {
                    setInviteForm(prev => ({ ...prev, email: e.target.value }));
                    if (inviteErrors.email) setInviteErrors(prev => ({ ...prev, email: null }));
                  }}
                  placeholder="team.member@company.com"
                  className={cn(inviteErrors.email && "border-red-500")}
                />
                {inviteErrors.email && <p className="text-sm text-red-500">{inviteErrors.email}</p>}
              </div>

              {/* Role */}
              <div className="space-y-3">
                <Label>Role & Permissions</Label>
                <RadioGroup
                  value={inviteForm.role}
                  onValueChange={(value) => setInviteForm(prev => ({ ...prev, role: value }))}
                  className="space-y-2"
                >
                  {roleOptions.map(role => (
                    <Label
                      key={role.value}
                      htmlFor={`role_${role.value}`}
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                        inviteForm.role === role.value
                          ? "bg-primary/10 border-primary/50"
                          : "bg-muted/30 border-border hover:bg-muted/50"
                      )}
                    >
                      <RadioGroupItem value={role.value} id={`role_${role.value}`} className="mt-1" />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{role.label}</div>
                        <div className="text-xs text-muted-foreground">{role.description}</div>
                      </div>
                    </Label>
                  ))}
                </RadioGroup>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="invite_message">Personal Message (Optional)</Label>
                <Textarea
                  id="invite_message"
                  value={inviteForm.message}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Welcome to the team!"
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteForm({ email: '', role: 'staff', message: '' });
                  setInviteErrors({});
                }}
                disabled={inviteLoading}
              >
                Cancel
              </Button>
              <Button onClick={handleInviteMember} disabled={inviteLoading}>
                {inviteLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Send Invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </PageContainer>
  );
};

export default TeamSection;
